import dayjs from 'dayjs';
import { enqueueSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import settings from '/@/core/settings';
import { revalidateMyFields, useFieldById } from '/@/hooks/fields';
import { _ } from '/@/locale';
import { setField } from '/@/store/crop-monitoring/actions';
import { getCropMonitoringById } from '/@/store/crop-monitoring/apiProviders';
import {
    setCropMonitoring,
    setEstimatedApplicationDate,
    setNitrogenSource,
    setPlantingDate,
    setProductName,
    setSelectedHybrid,
} from '/@/store/nitrogen-recommendation/actions';
import { axFetch } from '/@/store/session/actions';
import { getAppInstance, getSessionCountry } from '/@/store/session/selectors';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';
import {
    getCampaignArchiveIds,
    getCropMonitoringData,
    shouldPollPendingCropMonitoring,
} from '/@/utils/crop-monitoring';
import { invalidate } from '/@/utils/swr';
import type { HybridFormType } from '/@/views/fields/field-monitoring/components/creation-dialog/types';

const CM_PENDING_ASYNC_POLL_MS = 5_000;

export async function getCropMonitoring(cropMonitoringId: number) {
    const res = await axFetch.get(`${settings.urls.apiDomain}/core/v1/crop-monitoring/${cropMonitoringId}/`);
    return Promise.resolve(res);
}

export const getAndSetCropMonitoringOnNitrogenSmartRedux = async (dispatch, cropMonitoringId) => {
    const { data } = await getCropMonitoring(cropMonitoringId);
    const cropData = getCropMonitoringData(data);
    dispatch(setField(data.field));
    dispatch(setCropMonitoring(data));
    dispatch(setPlantingDate(data.plantingDate));
    dispatch(setNitrogenSource(cropData?.fertilizer));
    dispatch(setSelectedHybrid(cropData?.product));
    dispatch(setProductName(cropData?.productName));
    if (data.expectedV6Date) {
        dispatch(setEstimatedApplicationDate(cropData?.expectedV6Date));
    }

    return data;
};

/**
 * @typedef {Object} CropMonitoring
 * @property {number} id
 * @property {string} field
 * @property {any[]} stages
 * @property {number} user
 * @property {string} currentStage
 * @property {number} currentStagePercentage
 * @property {object} vegetativeStage
 * @property {any[]} nitrogenRecomendations
 * @property {string} created
 * @property {string} modified
 * @property {object} deleted_at
 * @property {string} plantingDate
 * @property {number} humidity
 * @property {number} environment
 * @property {number} density
 * @property {number} fertilizer_amount
 * @property {number} accumulatedGdu
 * @property {object} accumulatedGduDate
 * @property {object} expectedFloweringDate
 * @property {object} expectedMaturityDate
 * @property {boolean} active
 * @property {number} appInstance
 * @property {number} product
 * @property {number} fertilizer
 */

export const useCropMonitoringById = (id: number) => {
    const key = id ? { key: 'useCropMonitoringById', id } : null;

    return useSWR(key, () => getCropMonitoringById(id), {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        // After creating/updating a CM the backend computes phenology/GDU asynchronously. Poll
        // this CM only while it is still processing, so each hybrid refreshes on its own.
        refreshInterval: (data) => (shouldPollPendingCropMonitoring(data) ? CM_PENDING_ASYNC_POLL_MS : 0),
    });
};

/**
 * Invalidates all crop monitoring queries and related fields queries
 * @param cmId - The crop monitoring ID to invalidate
 * @param fieldId - The field ID to invalidate
 * @param country - The country code for field queries invalidation
 */
export const invalidateCropMonitoringQueries = ({
    cmId,
    fieldId,
    country,
}: {
    cmId?: number;
    fieldId: number;
    country: string;
}) => {
    // Invalidate specific CM if provided
    if (cmId) {
        invalidate({ key: 'useCropMonitoringById', id: cmId });
    }
    // Invalidate all CMs list
    invalidate({ key: 'allCropMonitorings' });
    // Invalidate specific field
    invalidate({ key: 'useFieldById', fieldId });
    // Invalidate all fields queries
    revalidateMyFields({ country, fieldId });
    // Invalidate all dashboard queries
    invalidate({ key: 'growersDashboardReports' });
};

/**
 * "Finalizar campaña": archives the crop monitoring together with every other active one of
 * its field (the hybrids of the campaign), all or nothing.
 */
export const useMutateArchiveCropMonitoringById = (cropMonitoring?: Pick<CropMonitoring, 'id' | 'field'>) => {
    const country = useSelector(getSessionCountry);
    const fieldQuery = useFieldById({ id: cropMonitoring?.field as number }, { enabled: !!cropMonitoring });
    const key = cropMonitoring ? { key: 'useMutateArchiveCropMonitoringById', id: cropMonitoring.id } : null;
    return useSWRMutation(key, async ({ id }) => {
        // The field lists the active hybrids; fetch it if nothing loaded it yet.
        const field: Field | undefined = fieldQuery.data ?? (await fieldQuery.mutate());
        const ids = getCampaignArchiveIds(id, field);
        const res = await axFetch.post<CropMonitoring[]>(
            `${settings.urls.apiDomain}/core/v1/crop-monitoring/bulk-archive/`,
            { ids }
        );
        for (const cmId of ids) invalidate({ key: 'useCropMonitoringById', id: cmId });
        invalidateCropMonitoringQueries({ fieldId: cropMonitoring?.field as number, country: country.code });
        return res.data;
    });
};

export const useMutateCreateOrUpdateCropMonitoring = (
    {
        id,
        field,
        crop,
        previousCrop,
        hybrids,
        // Legacy single-hybrid params (add-planting, density recommendation, registry edit).
        plantingDate,
        environment,
        plantedArea,
        density,
        product,
        technology,
    }: {
        id?: number;
        field: number;
        crop?: string;
        previousCrop?: string;
        hybrids?: HybridFormType[];
        plantingDate?: string;
        environment?: number;
        plantedArea?: number;
        density?: number;
        product?: number | string;
        technology?: number;
    },
    {
        onSuccess,
    }: {
        onSuccess?: (data: any) => void;
    }
) => {
    const appInstance = useSelector(getAppInstance)?.id;
    const country = useSelector(getSessionCountry);
    // Legacy callers pass flat fields; wrap them as a single hybrid.
    const effectiveHybrids: HybridFormType[] = hybrids?.length
        ? hybrids
        : [{ plantingDate, product, technology, environment, plantedArea, density, id }];
    const hybridsReady =
        effectiveHybrids.length > 0 &&
        effectiveHybrids.every((h) => h.plantingDate && h.environment && h.product !== undefined);
    const key =
        hybridsReady && field
            ? {
                  id,
                  field,
                  crop,
                  previousCrop,
                  hybrids: effectiveHybrids,
                  key: 'useMutateCreateOrUpdateCropMonitoring',
              }
            : null;

    return useSWRMutation(
        key,
        async ({ field, crop, previousCrop, hybrids }) => {
            const sharedPayload = {
                field: field,
                crop: crop,
                previous_crop: previousCrop || null,
                appInstance: appInstance,
            };
            const hybridPayload = (h: HybridFormType | undefined) => ({
                ...(h?.id != null ? { id: h.id } : {}),
                planting_date: h?.plantingDate ? dayjs(h.plantingDate).format('YYYY-MM-DD') : null,
                environment: h?.environment,
                planted_area: h?.plantedArea ?? null,
                density: h?.density ?? null,
                product: h?.product === -1 ? null : h?.product,
                technology: h?.technology ?? null,
            });

            // Create or edit, both go through the bulk endpoint (CUL-1505): each item
            // is its own CropMonitoring, carrying its `id` when it already exists (edit)
            // or none (create). One transaction -- if any item fails, none is saved.
            const res = await axFetch.put(`${settings.urls.apiDomain}/core/v1/crop-monitoring/bulk/`, {
                ...sharedPayload,
                items: (hybrids ?? []).map(hybridPayload),
            });
            const affected: { id: number; field: number }[] = res.data;

            // Invalidate every CM we sent (by id) and every CM we got back.
            const touchedIds = new Set<number>();
            for (const h of hybrids ?? []) {
                if (h?.id != null) touchedIds.add(h.id);
            }
            for (const cm of affected) {
                touchedIds.add(cm.id);
            }
            const affectedFieldId = affected[0]?.field ?? field;
            for (const cmId of touchedIds) {
                invalidateCropMonitoringQueries({
                    cmId,
                    fieldId: affectedFieldId,
                    country: country.code,
                });
            }
            return affected;
        },
        {
            onSuccess: (data) => {
                onSuccess?.(data);
            },
            onError: () => {
                enqueueSnackbar(_('Error al agregar el cultivo'), { variant: 'error' });
            },
        }
    );
};
